import React, { useState, useEffect } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Button, TextField, CircularProgress } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import NumberFormat from 'react-number-format';
import RequestApis from '../../apis/RequestApis';
import './RegisterPage.css';

const validationSchema = Yup.object({
    phoneNumber: Yup.string("Enter a valid phone number")
            .required('Phone Number is required')
            .trim(),
    accessCode: Yup.string("Enter an access code")
            .required('Access code is required')
            .trim()
});

const RegisterForm = props => {
    const {
        errors,
        handleChange,
        handleSubmit,
        formComplete,
        values,
        handleBlur,
        touched,
        errorMessage,
        addressForm,
        address,
        setFieldValue
    } = props;

    const [disableField, setDisableField] = useState(false);

    useEffect(() => {
        if (address.address) { 
            setFieldValue('address', address.address.normalizedAddress);
            setFieldValue('location', address.info);
            setDisableField(true);
        }
    }, [address]);
    
    return (
        <div>
            { !formComplete &&
                <form onSubmit={handleSubmit} autoComplete="off">
                    <NumberFormat 
                        disabled={disableField}
                        id="phoneNumber"
                        label="Phone Number"
                        margin="normal"
                        name="phoneNumber"
                        type="text"
                        onChange={handleChange}
                        value={values.phoneNumber}
                        onBlur={handleBlur}
                        fullWidth={true}
                        format="(###) ###-####" 
                        customInput={TextField}
                        mask="_"/>
                    <span id="phone-number-error" style={{ color: 'red', fontSize: '14px' }}>
                        {errors.phoneNumber && touched.phoneNumber && errors.phoneNumber}
                    </span>
                    <TextField
                        disabled={disableField}
                        id="accessCode"
                        label="Access Code"
                        margin="normal"
                        onChange={handleChange}
                        value={values.accessCode}
                        onBlur={handleBlur}
                        name="accessCode"
                        fullWidth={true}
                    />
                    <span id="access-code-error" style={{ color: 'red', fontSize: '14px' }}>
                        {errors.accessCode && touched.accessCode && errors.accessCode}
                    </span>
                    { addressForm &&    
                        <div>
                            <h3>Here is your current address. Please edit and submit if needed.</h3>
                            <TextField
                                id="address"
                                label="Address"
                                margin="normal"
                                onChange={handleChange}
                                value={values.address}
                                onBlur={handleBlur}
                                name="address"
                                fullWidth={true}
                            />
                            {errors.address && touched.address && errors.address}
                            <TextField
                                id="location"
                                label="Location Info"
                                placeholder="Ex: Apt, Room, Suite, etc."
                                margin="normal"
                                onChange={handleChange}
                                value={values.location}
                                onBlur={handleBlur}
                                name="location"
                                fullWidth={true}
                            />
                            {errors.location && touched.location && errors.location}
                        </div>
                    }
                    <br/>
                    <br/>
                    { !formComplete &&
                        <Button id="submit-button" className="primary" type="submit" variant="outlined" 
                            style={{ 
                                background: '#e92d37',
                                color: '#fff',
                                padding: '8px 20px',
                                border: 0
                            }}>Submit</Button>
                    }
                    { errorMessage && 
                        <div className="error-message">
                            <ErrorIcon/>
                            <span>{errorMessage}</span>
                        </div> }
                </form>
            }
            { formComplete &&
                <h3 className="form-completed-message" style={{ color: '#65c135', textAlign: 'center' }}>Thank you. Your address has been updated. <br/>Please close your browser.</h3>
            }
        </div>
    );
}

const RegisterPage = props => {
    const [loading, setLoading] = useState(false);
    const [addressForm, setAddressForm] = useState(false);
    const [formComplete, setFormComplete] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [address, setAddress] = useState({});
    const [token, setToken] = useState('');

    function handleFormSubmit(values) {
        function errorHandler(error) {
            if (error.response && error.response.status === 401) {
                setErrorMessage('Your session has expired. Please refresh your browser and start again.');
            } else if (error.response && error.response.data.title) {
                setErrorMessage(error.response.data.title);
            } else {
                setErrorMessage('The server is having issues. Please try again later.');
            }
        }
        
        function errorHandlerRegisterApi(error) {
            if (error.response && error.response.status === 401) {
                setErrorMessage('Invalid phone number or access code.');
            } else if (error.response && error.response.data.title) {
                setErrorMessage(error.response.data.title);
            } else {
                setErrorMessage('The server is having issues. Please try again later.');
            }
        }

        setLoading(true);
        if (!addressForm) {
            RequestApis.register(values).then(response => {
                setToken(response.data);
                RequestApis.getLocation(values, response.data).then(response => {
                    setAddress(response.data);
                    setLoading(false);
                    setAddressForm(true);
                }).catch(error => {
                    errorHandlerRegisterApi(error);
                    setLoading(false);
                });
            }).catch(error => {
                errorHandlerRegisterApi(error);
                setLoading(false);
            })
        } else {
            values.name = address.name;
            values.id = address.id;
            RequestApis.editLocation(values, token).then(() => {
                setLoading(false);
                setFormComplete(true);
            }).catch(error => {
                errorHandler(error);
                setLoading(false);
            });
        }
    }
    
    return (
        <div className="register-page" style={{
            maxWidth: formComplete ? 'none' : '770px'
        }}>
            { !formComplete &&
                <h1 className="self-provisioning-portal-header">Self-Provisioning Portal</h1>
            }
            { loading && <CircularProgress className="loading-bar" /> }
            <Formik
                initialValues={{
                    phoneNumber: '',
                    accessCode: '',
                    address: '',
                    location: ''
                }}
                onSubmit={(values) => {
                    setErrorMessage('');
                    values.phoneNumber = values.phoneNumber.replace(/\D/g,"");
                    handleFormSubmit(values);
                }}
                validationSchema={validationSchema}
                render={formikProps => 
                    <RegisterForm 
                        address={address}
                        setAddress={setAddress}
                        addressForm={addressForm}
                        setAddressForm={setAddressForm}
                        formComplete={formComplete}
                        setFormComplete={setFormComplete}
                        errorMessage={errorMessage}
                        setErrorMessage={setErrorMessage}
                        {...formikProps} 
                    />
                }
                />
        </div>
    );
}

export default RegisterPage;